import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _LocaleManager;
import { LocaleTagUtils } from "abweb/service/locale/LocaleTagUtils";
import { BrowserUtils } from "abweb/utils/BrowserUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { URLUtils } from "abweb/utils/URLUtils";

/*
 * Higher level doc written in answer to a webex chat question: about ai_locale and ai_locale_preferred_cookie:
 * 
 * ai_locale_preferred_cookie is set when the user has explicitly save a locale preference via the the 'Choose Language' 
 * dialog. If null, then the ai_locale negotiation on each session will negotiate with the server to find the 
 * best supported locale for ai_locale. This cookie persists across sessions.
 *
 * ai_locale is the cookie denoting the locale to use for the current login session. It only persists for the 
 * current login session.
 *
 * From AbInitioLocaleFilter.java javadoc:
 *   The locale of a request is calculated as follows:
 *   <ol>
 *   <li>If the request that contains the parameter {@link LocaleUtils#LOCALE_SET ai_locale}, that value is used.</li>
 *   <li>
 *   If the request that contains a cookie named {@link LocaleUtils#LOCALE_SET ai_locale}, that value is used.
 *   This will have either have been calculated as the default in a previous request, or is the value of the
 *   {@code ai_locale} parameter passed in earlier from above.
 *   </li>
 *   <li>
 *   If the request that contains a cookie named {@link LocaleUtils#LOCALE_PREFERRED ai_locale_preferred},
 *   that value is used. This is the user specified locale preference.
 *   </li>
 */

export const DEFAULT_LANGUAGE_LOCALE = "en";
// Builtins lang locales already client normalized (has '-' and not '_')
const BUILTIN_LANGUAGE_LOCALES = ["en", "es", "fr", "ja", "zh-CN", "de", "es"];

// Use browser-determined 'locale' value for date/time and number locales.
export const DEFAULT_DATETIME_LOCALE = new Intl.DateTimeFormat().resolvedOptions().locale;
export const DEFAULT_NUMBER_LOCALE = new Intl.NumberFormat().resolvedOptions().locale;

// Use Typed Function instead of interface so that static classes can 'implement' the Locale Listener contract
// Async as may need to load files (e.g., resource bundles, kendo cultures) as a result of locale change

const DEFAULT_LOCALE_STATE = {
  languageLocale: DEFAULT_LANGUAGE_LOCALE,
  numberFormatLocale: DEFAULT_NUMBER_LOCALE,
  datetimeFormatLocale: DEFAULT_DATETIME_LOCALE
};
const LOG = LogUtils.getLogger(__filename);

/**
 * Manages determining the current locale, and notifying listeners if the current locale changes.
 * It is NOT a service as NumberUtils and DateUtils depend on it,
 * and they can conceivably be used before AbWebServices is even initialized.
 */
export class LocaleManager {
  constructor() {
    throw new Error("LocaleManager is a static class!");
  }

  // LocaleState property implementation

  static get languageLocale() {
    return LocaleManager._localeState.languageLocale;
  }
  static get preferredLanguageLocale() {
    return LocaleManager._preferredLanguageLocale;
  }
  static get languageLocaleParam() {
    // AbInitioLocaleFilter processes ai_locale param, and expects '-' and not underscores (zh-CN breaks, while zh_CN works)
    return LocaleManager._localeState.languageLocale.replaceAll("-", "_");
  }

  /*
  A locale mapper can be set to handle situations where a locale that works for language and date formatting
  (which is performed by kendo culture) does not work for number formatting. A locale mapper must return the
  locale passed in or some other desired locale alternative that works for 'formatting'.  As an example,
  given 'en_FR' as the locale string, it would return 'en' as the locale to use for resource string lookup.
   */
  static createLocaleStateFromLangLocale(langLocale) {
    return {
      languageLocale: langLocale,
      numberFormatLocale: DEFAULT_NUMBER_LOCALE,
      datetimeFormatLocale: DEFAULT_DATETIME_LOCALE
    };
  }

  /**
   * @Deprecated use languageLocale instead
   */
  static get locale() {
    return LocaleManager.languageLocale;
  }
  static get numberFormatLocale() {
    return LocaleManager._localeState.numberFormatLocale;
  }
  static get datetimeFormatLocale() {
    return LocaleManager._localeState.datetimeFormatLocale;
  }

  /**
   * Allows caller to change the locale state to override the normal negotiated locale.
   * MHub invokes this to override number/date locale, and TRW for overriding language according to
   * GDE-supplied anchor param 'lang=ja'.
   * If it changes the Localemanager.localeState members, it will update the rest of infrastructure accordingly:
   * <ol>
   * <li>Changed datetimeLocale will update (Date|Time|Calendar)Utils default formatting
   * <li>Changed numberLocale will update NumberUtils default formatting
   * <li>Changed languageLocale will cause already loaded resources bundles to be reloaded for new new languageLocale
   * </ol>
   */
  static setCustomLocaleStateMapper(localeStateMapper) {
    if (LocaleManager.customLocaleStateMapper && localeStateMapper) {
      // defensive: unsupported use case if have multiple callers all trying to be the customLocaleStateMapper
      throw new Error("Can not clobber an existing customLocaleStateMapper");
    }
    LocaleManager.customLocaleStateMapper = localeStateMapper;
    LocaleManager._recalcLocale = true;
    return LocaleManager.setLocaleStateFromLanguageLocale(LocaleManager._localeState.languageLocale);
  }
  static get localeCSSClassName() {
    return `ab-locale-${LocaleManager.languageLocale}`;
  }
  static resetStatics() {
    LocaleManager.listeners = new Array();
    LocaleManager.registerLocaleListener(LocaleManager.changeHtmlDocLangListener);
    LocaleManager.customLocaleStateMapper = undefined;
    LocaleManager._localeState = DEFAULT_LOCALE_STATE;
    LocaleManager.setLocaleFromCookie(); // fires listeners (empty at this point)
  }
  static setLocaleFromCookie() {
    LocaleManager._preferredLanguageLocale = LocaleTagUtils.normalizeLocaleTagForClient(URLUtils.readCookie(LocaleManager.PREFERRED_LOCALE_COOKIE_NAME));
    const locale = LocaleManager.localeDetection();
    return LocaleManager.setLocaleStateFromLanguageLocale(locale);
  }

  // There is NO SETTER (readonly) for locale, as can ony be set from setLocaleFromCookie

  /**
   * Register listener, which will be invoked on every locale change
   */
  static registerLocaleListener(listener) {
    LocaleManager.listeners.push(listener);
  }

  /**
   * Register and invoke listener in one atomic operation (simple api from caller perspective).
   * listener will immediately be invoked, and then re-invoked on every locale change
   */
  static registerAndInvokeLocaleListener(listener) {
    LocaleManager.registerLocaleListener(listener);
    return listener(LocaleManager.currentLocaleStateValue());
  }

  /** Syntactic sugar to adapt sync locale listener to async listener contract */
  static registerAndInvokeSyncLocaleListener(syncListener) {
    // Do NOT invoke 'registerAndInvokeLocaleListener' above as no guarantee async listener invocation will complete
    // before this method returns...  Instead, directly invoke the syncListener.
    LocaleManager.registerLocaleListener(localeState => {
      syncListener(localeState);
      return Promise.resolve();
    });
    syncListener(LocaleManager.currentLocaleStateValue());
  }
  static async changeHtmlDocLangListener(localeState) {
    // CAT-1009 add 'html lang' attribute support.  We *think* this should work in iFrames, but we haven't tested...
    /* Adding in the Locale string to the :root (<HTML> tag) so CSS Variable Overrides will work swimmingly */
    /* Note:  We *think* this should work in iFrames, but haven't been able to test it */
    document.documentElement.className += " " + LocaleManager.localeCSSClassName;
    BrowserUtils.setDocumentLang(localeState.languageLocale);
  }

  // Default is it sets localeState to new language locale, with default formatting locale (default browser locale).
  // However, products can provide a customLocaleStateMapper to modify the new locale state as well (only mhub/ag uses this)
  // before the new locale state is set and corresponding locale state listeners fired.
  // Also directly invoked by tests (via 'any' casting):
  static async setLocaleStateFromLanguageLocale(newLangLocale) {
    let localeState = LocaleManager.createLocaleStateFromLangLocale(newLangLocale);
    if (LocaleManager.customLocaleStateMapper) {
      localeState = LocaleManager.customLocaleStateMapper(localeState);
    }
    await LocaleManager.setLocaleState(localeState);
    return this.languageLocale;
  }

  // Products production code should NOT call this unless get ok from Brad
  // Use case: Only public as mhub/ag uses this to force all swedbank users to use 'sv' formatting locale.
  // Also directly invoked by tests
  static async setLocaleState(newLocaleState) {
    const prettyLocaleState = JSON.stringify(newLocaleState);
    if (!LocaleManager._recalcLocale && LocaleManager.localeStateEquals(newLocaleState, LocaleManager._localeState)) {
      LOG.info("No change to the current locale state: " + prettyLocaleState);
      return;
    }
    LocaleManager._localeState = newLocaleState;
    LOG.info("Current language locale: " + LocaleManager.languageLocale);
    LocaleManager._recalcLocale = false;
    await LocaleManager.fireLocaleListeners(); // fires changeHtmlDocLangListener as well
  }
  static localeStateEquals(localeState1, localeState2) {
    return localeState1?.languageLocale === localeState2?.languageLocale && localeState1?.numberFormatLocale === localeState2?.numberFormatLocale && localeState1?.datetimeFormatLocale === localeState2?.datetimeFormatLocale;
  }

  /**
   * Return either actual negotiated locale (source from ai_locale cookie),
   * or best guess as to what the eventual negotiated ai_locale cookie will be (calculated by AbInitioLocaleFilter.java).
   * If all else fails, return default locale of "en".
   */
  static localeDetection() {
    // Try to use abinitio maintained locale, which is returned via ai_locale cookie from server (which in turn
    // may be determined by server from a session-independent ai_preferred_locale cookie)
    // Do NOT read cached cookie as the locale can change between login screen and subsequent user preferred locale
    //
    // During karma tests, can not read cookies from javascript,
    // so URLUtils will always return 'en' (see URLUtils.TestCookies)
    let locale = LocaleTagUtils.normalizeLocaleTagForClient(URLUtils.readCookie(LocaleManager.LOCALE_COOKIE_NAME));

    // Below is best guess as to what eventual negotiated locale (ai_locale cookie) will eventually be.
    // By making an accurate guess, it reduces need to reload resource bundles with a different locale once
    // the negotiated locale is available.
    if (!locale) {
      locale = LocaleTagUtils.normalizeLocaleTagForClient(URLUtils.readCookie(LocaleManager.PREFERRED_LOCALE_COOKIE_NAME));
      if (locale) {
        LOG.info(`No Ab Initio Locale cookie defined.  Defaulting to preferred language locale: '${locale}'`);
      } else {
        locale = LocaleManager.acceptLanguageMatch();
        if (locale) {
          LOG.info(`No Ab Initio Locale cookie defined.  Defaulting to locale that matches Accept-Language header: '${locale}'`);
        } else {
          locale = DEFAULT_LANGUAGE_LOCALE;
          LOG.info(`No Ab Initio Locale cookie defined.  Defaulting to default language locale: '${locale}'`);
        }
      }
    }
    return locale;
  }

  /**
   * Best guess as to what the server will eventually set as the negotiated locale cookie.
   * It is based on request accept-language header contents (which are accessed in javascript via navigator.languages).
   * It only supports the core Ab Initio languages, and does not take into account any 'custom' locales
   * supported by a particular app server deployment.
   */
  static acceptLanguageMatch() {
    // This method is only typically invoked for deployments where a web server (e.g. nxinx)
    // serves up initial static content (.../index.html, *.js, ...), as that bypasses the
    // appserver's AbInitioLocaleFilter so no initial locale cookie is set.  The locale returned by this is typically
    // used to load the initial abweb/abreact resource bundles, after which the first rpc (prodInfo) is invoked which
    // causes the app server to specify the locale cookie.  If the server locale cookie is different, the startup
    // code will reload the abweb/abreact resource bundles accordingly (hopefully the 'guess' is correct so as to
    // minimize the need to reload the abweb/abreact resource bundles!).  As such, this method internally
    // mirrors the appserver AbInitioLocaleFilter heuristic for determining the locale cookie.
    const acceptLanguages = navigator.languages;
    for (const lang of acceptLanguages) {
      const builtin = BUILTIN_LANGUAGE_LOCALES.find(builtinLang => lang.startsWith(builtinLang));
      if (builtin) {
        return builtin;
      }
    }
    return null;
  }
  static async fireLocaleListeners() {
    // Guarantee that all listeners updated w/same locale by creating copy of current LocaleState
    // Both resource bundles and kendo culture files may need to be loaded async.
    const currentLocaleState = LocaleManager.currentLocaleStateValue();
    const currentListeners = [...LocaleManager.listeners];

    // Must sequentially invoke listeners, as there are sequential dependencies (such as Kendo culture needs to be
    // refreshed before the column renderers (which are implicitly dependent on current kendo culture) are refreshed).
    for (let listener of currentListeners) {
      await listener(currentLocaleState);
    }
  }

  /** @return current LocaleState in the LocaleManager */
  static currentLocaleStateValue() {
    return LocaleManager._localeState;
  }
}
_LocaleManager = LocaleManager;
_defineProperty(LocaleManager, "LOCALE_REQUEST_PARAM", "ai_locale");
// Server returns negotiated Language locale via these cookies
_defineProperty(LocaleManager, "LOCALE_COOKIE_NAME", "ai_locale");
_defineProperty(LocaleManager, "PREFERRED_LOCALE_COOKIE_NAME", "ai_locale_preferred");
_defineProperty(LocaleManager, "listeners", new Array());
_defineProperty(LocaleManager, "_preferredLanguageLocale", null);
_defineProperty(LocaleManager, "_recalcLocale", true);
_defineProperty(LocaleManager, "_localeState", DEFAULT_LOCALE_STATE);
_defineProperty(LocaleManager, "customLocaleStateMapper", void 0);
// Initialize immediately so NumberUtils/DateUtils (which use locale) can run before the real user locale is known.
_defineProperty(LocaleManager, "staticInitializer", _LocaleManager.resetStatics());
//# sourceMappingURL=LocaleManager.js.map
